import { authRequest } from './request'

/**
 * Asking for access, in VentaWiz-Auth (its ADR 0015): somebody refused a
 * page asks for a role or a permission on a place, saying why; the people
 * whose `grants:edit` reaches that place approve or reject it in User
 * Management. The service owns the requests and the grants — this app only
 * asks, with the VentaWiz session, and keeps no copy.
 */

/** What is asked for: a role (`bundle`) or one permission. */
export type SubjectKind = 'bundle' | 'permission'

/** The kinds of entity a VentaWiz grant can be scoped to. */
export type AccessKind =
  'store' | 'marketplace' | 'brand' | 'warehouse' | 'vendor' | 'retailer' | 'service_provider'

/** Where access is asked for: an entity, or — with none — the whole organisation. */
export type RequestPlace = { kind: AccessKind; id: number } | null

export type NewAccessRequest = {
  subject_kind: SubjectKind
  subject_ref: string
  place: RequestPlace
  reason: string
}

/** The service's answer: the request, pending until somebody decides. */
export type AccessRequest = {
  id: number
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
}

/** `POST /v1/access-requests` — for the signed-in person themselves. */
export function requestAccess({ place, ...body }: NewAccessRequest): Promise<AccessRequest> {
  return authRequest<AccessRequest>('/access-requests', {
    method: 'POST',
    body: JSON.stringify({
      application: 'ventawiz',
      ...body,
      ...(place ? { scope_kind: place.kind, scope_id: place.id } : {}),
    }),
  })
}

/** One VentaWiz permission in the public catalogue. */
export type CataloguePermission = {
  code: string
  label: string
  /** The scope kinds it may be granted on; empty means organisation-wide only. */
  scopes: string[]
}

/** `GET /v1/catalogue`: VentaWiz's permissions and where each may be granted. Public. */
export async function catalogue(): Promise<CataloguePermission[]> {
  const body = await authRequest<{ permissions: CataloguePermission[] }>(
    '/catalogue?application=ventawiz',
  )
  return body.permissions
}

/** A role: a named set of permissions, granted as one row. */
export type Bundle = { id: number; name: string; label: string }

/**
 * `GET /v1/roles/bundles`: the organisation's VentaWiz roles. Usually refused
 * to somebody with no access to anything; the permissions are enough to ask
 * with, so a refusal only means no roles are offered.
 */
export async function listBundles(): Promise<Bundle[]> {
  const body = await authRequest<{ bundles: Bundle[] }>('/roles/bundles?application=ventawiz')
  return body.bundles
}

// ── A person's own access (VentaWiz-Auth ADR 0022) ───────────────────────

/** The kinds a request can name, in the order a picker offers them. */
export const ACCESS_KINDS: readonly AccessKind[] = [
  'store',
  'brand',
  'marketplace',
  'warehouse',
  'retailer',
  'service_provider',
  'vendor',
]

/** One role, group or permission an entry of one's access is made of. */
export type MyAccessSubject = { kind: string; ref: string; label: string | null }

/** One's access on one place — or every place of a kind, or the organisation. */
export type MyAccessEntry = {
  scope_kind: string | null
  scope_id: number | null
  scope_label: string | null
  /** For "All <kind>s", the kind (`scope_id` is then `-1`). */
  all_of_kind: string | null
  level: string | null
  /** The level's or role's name, or `Custom` for a mixture. */
  label: string
  subjects: MyAccessSubject[]
  is_admin: boolean
  expires_at: string | null
}

export type MyApplicationAccess = {
  application: string
  application_name: string
  has_places: boolean
  entries: MyAccessEntry[]
}

export type MyAccess = { organisation: string; applications: MyApplicationAccess[] }

/** `GET /v1/me/access`: the signed-in person's live access in this organisation. */
export function myAccess(): Promise<MyAccess> {
  return authRequest<MyAccess>('/me/access')
}

/** A place a request can name. */
export type RequestablePlace = { kind: AccessKind; scope_id: number; label: string }

/**
 * `GET /v1/me/requestable-places`: the organisation's places of one kind whose
 * name contains `search` — at least two characters, twenty at a time.
 */
export async function requestablePlaces(
  kind: AccessKind,
  search: string,
): Promise<RequestablePlace[]> {
  const query = new URLSearchParams({ kind, search })
  const body = await authRequest<{ places: RequestablePlace[] }>(
    `/me/requestable-places?${query.toString()}`,
  )
  return body.places
}

/** A role that can be asked for on a place: one of the kind's levels, or a custom role. */
export type RoleOffer = { bundle_id: number; name: string; label: string; level: string | null }

/**
 * `GET /v1/access/levels`: the roles for one place — Viewer, Manager, Admin
 * and the organisation's custom roles for the kind. Any member may read it,
 * unlike the roles list, so a person with nothing yet can still choose one.
 */
export async function roleOffers(kind: AccessKind, scopeId: number): Promise<RoleOffer[]> {
  const query = new URLSearchParams({
    application: 'ventawiz',
    kind,
    scope_id: String(scopeId),
  })
  const body = await authRequest<{ levels: RoleOffer[]; custom: RoleOffer[] }>(
    `/access/levels?${query.toString()}`,
  )
  return [...body.levels, ...body.custom]
}

/** One of one's own requests, as `GET /v1/access-requests/mine` lists it. */
export type MyAccessRequest = AccessRequest & {
  application: string
  subject_kind: string
  subject_ref: string
  scope_kind: string | null
  scope_id: number | null
  scope_label?: string | null
  requested_at: string
  decision_note?: string
}

/** `GET /v1/access-requests/mine`: one's own requests, newest first. */
export async function myRequests(): Promise<MyAccessRequest[]> {
  const body = await authRequest<{ requests: MyAccessRequest[] }>('/access-requests/mine')
  return body.requests
}

/** `POST /v1/access-requests/{id}/cancel`: withdraw one's own pending request. */
export function cancelRequest(id: number): Promise<MyAccessRequest> {
  return authRequest<MyAccessRequest>(`/access-requests/${id}/cancel`, { method: 'POST' })
}
